import { useCallback, useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'

export type AccountType = 'recruiter' | 'organisme'

export interface CompanyProfile {
  _id: string
  creator: string
  creatorEmail?: string
  accountType?: AccountType
  raisonSociale: string
  siret: string
  adresse: string
  telephone: string
  responsableNom: string
  responsableFonction?: string
  /** URL OSS du logo (optionnel mais encouragé) */
  logoUrl?: string
  createdAt: string
  updatedAt: string
}

export interface CompanyProfileInput {
  raisonSociale: string
  siret: string
  adresse: string
  telephone: string
  responsableNom: string
  responsableFonction?: string
  logoUrl?: string
}

/**
 * Renvoie true si toutes les coordonnées obligatoires sont présentes.
 */
export function isCompanyProfileComplete(p: Partial<CompanyProfile> | null | undefined): boolean {
  if (!p) return false
  // SIRET et adresse complète sont désormais optionnels : ils ne bloquent plus
  // la publication d'une annonce. Seuls les champs essentiels restent requis.
  return Boolean(
    p.raisonSociale?.trim() &&
      p.telephone?.trim() &&
      p.responsableNom?.trim()
  )
}

/**
 * Récupération directe (sans état React) — utile pour l'admin.
 */
export async function fetchCompanyProfileByCreator(
  creator: string
): Promise<CompanyProfile | null> {
  if (!creator) return null
  try {
    const { list } = await lumi.entities.company_profiles.list({
      filter: { creator },
      sort: { updatedAt: -1 },
      limit: 1,
    })
    const arr = list as CompanyProfile[]
    return arr[0] || null
  } catch (e) {
    console.error('[company_profiles] fetchByCreator failed', e)
    return null
  }
}

/**
 * Récupère en une requête les logos (et raisons sociales) d'une liste de creators.
 * Utilisé sur les pages publiques pour enrichir les offres avec le logo du recruteur.
 */
export async function fetchCompanyProfilesByCreators(
  creators: string[]
): Promise<Record<string, { logoUrl?: string; raisonSociale?: string }>> {
  const unique = Array.from(new Set((creators || []).filter(Boolean)))
  if (unique.length === 0) return {}
  try {
    const { list } = await lumi.entities.company_profiles.list({
      filter: { creator: { $in: unique } },
      sort: { updatedAt: -1 },
    })
    const map: Record<string, { logoUrl?: string; raisonSociale?: string }> = {}
    for (const doc of (list as CompanyProfile[])) {
      // Premier match par creator (sort updatedAt desc => le plus récent gagne)
      if (doc.creator && !map[doc.creator]) {
        map[doc.creator] = {
          logoUrl: doc.logoUrl,
          raisonSociale: doc.raisonSociale,
        }
      }
    }
    return map
  } catch (e) {
    console.error('[company_profiles] fetchByCreators failed', e)
    return {}
  }
}

export function useCompanyProfile(creator?: string, email?: string, accountType?: AccountType) {
  const [profile, setProfile] = useState<CompanyProfile | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!creator) return
    setLoading(true)
    setError(null)
    try {
      const found = await fetchCompanyProfileByCreator(creator)
      setProfile(found)
    } catch (e) {
      console.error(e)
      setError('Chargement des coordonnées impossible.')
    } finally {
      setLoading(false)
    }
  }, [creator])

  useEffect(() => {
    if (creator) refresh()
  }, [creator, refresh])

  const upsert = useCallback(
    async (input: CompanyProfileInput): Promise<CompanyProfile | null> => {
      if (!creator) return null
      setSaving(true)
      setError(null)
      const now = new Date().toISOString()
      try {
        if (profile?._id) {
          const patch = {
            ...input,
            logoUrl: input.logoUrl ?? profile.logoUrl ?? '',
            updatedAt: now,
          }
          await lumi.entities.company_profiles.update(profile._id, patch)
          const next: CompanyProfile = { ...profile, ...patch }
          setProfile(next)
          return next
        } else {
          const doc = {
            ...input,
            logoUrl: input.logoUrl || '',
            creator,
            creatorEmail: email || '',
            accountType: accountType || 'recruiter',
            createdAt: now,
            updatedAt: now,
          }
          const created = (await lumi.entities.company_profiles.create(
            doc
          )) as CompanyProfile
          // Lumi retourne un _id sous forme string ou { $oid }
          const normalizedId = String(
            (created as unknown as { _id?: string | { $oid?: string } })?._id &&
              typeof (created as unknown as { _id?: string | { $oid?: string } })._id ===
                'object'
              ? ((created as unknown as { _id: { $oid: string } })._id.$oid)
              : ((created as unknown as { _id: string })._id)
          )
          const finalDoc: CompanyProfile = { ...(created as CompanyProfile), _id: normalizedId }
          setProfile(finalDoc)
          return finalDoc
        }
      } catch (e) {
        console.error('[company_profiles] upsert failed', e)
        setError("Enregistrement impossible.")
        return null
      } finally {
        setSaving(false)
      }
    },
    [creator, email, accountType, profile]
  )

  return {
    profile,
    loading,
    saving,
    error,
    refresh,
    upsert,
    isComplete: isCompanyProfileComplete(profile),
  }
}
